/**
 * Schémas Zod du domaine (tenant) — partagés API ⇄ ERP.
 *
 * Couvre la feature B.5.1 (édition fiche domaine) et préfigure B.3
 * (onboarding wizard) qui réutilisera les mêmes atomes.
 *
 * Convention :
 * - Tous les champs administratifs (SIREN/SIRET/TVA/accise/NAF) sont
 *   STOCKÉS NORMALISÉS : majuscules, sans espaces ni points. Les
 *   `.transform()` se chargent du nettoyage avant validation.
 * - Les champs nullables côté BDD sont `.optional()` côté schéma. La
 *   couche infra applique `?? null` au moment de l'INSERT/UPDATE.
 * - Tous les messages d'erreur sont en français — affichés tels quels
 *   sous le champ via react-hook-form.
 */

import { z } from "zod";

import {
  isValidCodeNaf,
  isValidCodePostalFr,
  isValidCommuneInsee,
  isValidNumeroAccise,
  isValidSiren,
  isValidSiret,
  isValidTvaIntraFr,
  normalizeCodeNaf,
} from "./validation.js";

// ────────────────────────────────────────────────────────────────────────────
// Helpers de composition
// ────────────────────────────────────────────────────────────────────────────

/**
 * Champ texte optionnel : vide → undefined. Évite d'envoyer "" au backend
 * pour des champs nullables (le couple "vide" / "absent" doit être unifié).
 */
function optionalTrim(max: number, errorMessage?: string) {
  return z
    .string()
    .trim()
    .max(max, errorMessage ?? `Trop long (${max} caractères max).`)
    .transform((v) => (v === "" ? undefined : v))
    .optional();
}

// ────────────────────────────────────────────────────────────────────────────
// Champs atomiques (réutilisables)
// ────────────────────────────────────────────────────────────────────────────

/** Statuts professionnels CIVC exposés en V0. MA et ND volontairement exclus. */
export const STATUTS_PROFESSIONNELS_V0 = ["RM", "NM", "CM", "RC", "SR"] as const;
export type StatutProfessionnelV0 = (typeof STATUTS_PROFESSIONNELS_V0)[number];

/** Préférences de navigation, indépendantes du statut CIVC et des permissions. */
export const ACTIVITES_DOMAINE = {
  vignoble_vendanges: "Vignoble et vendanges",
  pressurage: "Pressurage",
  vinification_assemblages: "Vinification et assemblages",
  elaboration_bouteilles: "Tirage, vieillissement et dégorgement",
  habillage: "Habillage",
  commercialisation: "Commercialisation",
} as const;

export const MODES_ACTIVITE = {
  au_domaine: "Au domaine",
  confie: "Confié à un tiers",
  les_deux: "Au domaine et chez un tiers",
  non_pratique: "Non pratiqué",
} as const;

const modeActiviteSchema = z.enum(["au_domaine", "confie", "les_deux", "non_pratique"]);
export const activitesDomaineSchema = z
  .object({
    vignoble_vendanges: modeActiviteSchema,
    pressurage: modeActiviteSchema,
    vinification_assemblages: modeActiviteSchema,
    elaboration_bouteilles: modeActiviteSchema,
    habillage: modeActiviteSchema,
    commercialisation: modeActiviteSchema,
  })
  .strict();
export type ActivitesDomaine = z.infer<typeof activitesDomaineSchema>;

/** Un ancien réglage incomplet ne doit jamais masquer une activité par défaut. */
export function normaliserActivitesDomaine(value: unknown): ActivitesDomaine | null {
  const resultat = activitesDomaineSchema.safeParse(value);
  return resultat.success ? resultat.data : null;
}

export const statutProfessionnelSchema = z.enum(STATUTS_PROFESSIONNELS_V0, {
  errorMap: () => ({ message: "Statut professionnel invalide." }),
});

export const raisonSocialeSchema = z
  .string()
  .trim()
  .min(1, "Raison sociale requise.")
  .max(255, "Raison sociale trop longue (255 caractères max).");

/**
 * N° d'agrément CIVC (sans le préfixe statut).
 * Format réel observé : 4 à 7 chiffres (la fourchette du schéma est prudente).
 * Optionnel tant que l'agrément n'est pas délivré (cas NM en cours d'agrément).
 */
export const civcNumeroSchema = z
  .string()
  .trim()
  .regex(/^\d{4,7}$/, "N° CIVC invalide (4 à 7 chiffres).")
  .transform((v) => (v === "" ? undefined : v))
  .optional()
  .or(z.literal("").transform(() => undefined));

/** SIREN : 9 chiffres + Luhn (exception La Poste gérée par `isValidSiren`). */
export const sirenSchema = z
  .string()
  .trim()
  // On retire les espaces utilisateur (souvent saisis par groupes de 3).
  .transform((v) => v.replace(/\s/g, ""))
  .refine((v) => v === "" || isValidSiren(v), {
    message: "SIREN invalide (9 chiffres avec clé de contrôle).",
  })
  .transform((v) => (v === "" ? undefined : v))
  .optional();

export const siretSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s/g, ""))
  .refine((v) => v === "" || isValidSiret(v), {
    message: "SIRET invalide (14 chiffres avec clé de contrôle).",
  })
  .transform((v) => (v === "" ? undefined : v))
  .optional();

/** TVA intracommunautaire France : `FR` + 2 caractères clé + 9 chiffres SIREN. */
export const tvaIntraSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((v) => v.replace(/\s/g, ""))
  .refine((v) => v === "" || isValidTvaIntraFr(v), {
    message: "N° TVA invalide (format attendu : FR + 11 caractères).",
  })
  .transform((v) => (v === "" ? undefined : v))
  .optional();

/** N° accise Douanes : `FR0` + 5 chiffres + lettre statut (E ou W) + 4 chiffres. */
export const numeroAcciseSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((v) => v.replace(/\s/g, ""))
  .refine((v) => v === "" || isValidNumeroAccise(v), {
    message: "N° d'accise invalide (FR0 + 5 chiffres + E ou W + 4 chiffres).",
  })
  .transform((v) => (v === "" ? undefined : v))
  .optional();

/** Code NAF/APE — accepte `01.21Z` mais stocke `0121Z`. */
export const codeNafSchema = z
  .string()
  .trim()
  .transform(normalizeCodeNaf)
  .refine((v) => v === "" || isValidCodeNaf(v), {
    message: "Code APE invalide (4 chiffres + 1 lettre, ex : 0121Z).",
  })
  .transform((v) => (v === "" ? undefined : v))
  .optional();

export const codePostalSchema = z
  .string()
  .trim()
  .refine((v) => v === "" || isValidCodePostalFr(v), {
    message: "Code postal invalide (5 chiffres).",
  })
  .transform((v) => (v === "" ? undefined : v))
  .optional();

export const communeInseeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => v === "" || isValidCommuneInsee(v), {
    message: "Code commune INSEE invalide (5 caractères, Corse 2A/2B accepté).",
  })
  .transform((v) => (v === "" ? undefined : v))
  .optional();

// Note : pas de `departement` dans le payload — il est dérivé automatiquement
// du code commune INSEE côté serveur (cf `deriveDepartementFromInsee` dans
// `validation.ts`). Le client n'a donc pas à le saisir.

// ────────────────────────────────────────────────────────────────────────────
// Payload tRPC : domaine.updateMyDomaine
// ────────────────────────────────────────────────────────────────────────────

/**
 * Payload accepté par l'endpoint `domaine.updateMyDomaine`.
 *
 * Tous les champs sauf `raison_sociale` et `statut_professionnel` sont
 * optionnels — une fiche peut rester partiellement remplie en V0 (un RM en
 * installation n'a pas encore son n° d'agrément CIVC, par ex.). Les modules
 * en aval (étiquette, DRM) bloqueront leur exécution si leurs prérequis
 * ne sont pas satisfaits, c'est leur responsabilité.
 */
export const updateMyDomaineInput = z.object({
  // Identité commerciale
  raison_sociale: raisonSocialeSchema,
  email_contact: z
    .string()
    .trim()
    .toLowerCase()
    .email("Format email invalide.")
    .max(320, "Email trop long.")
    .transform((v) => (v === "" ? undefined : v))
    .optional()
    .or(z.literal("").transform(() => undefined)),
  telephone_contact: optionalTrim(32, "Téléphone trop long (32 caractères max)."),

  // Statut + agrément CIVC
  statut_professionnel: statutProfessionnelSchema,
  civc_numero: civcNumeroSchema,
  // Omission : conserver ; null : revenir à une navigation non configurée.
  activites: activitesDomaineSchema.nullable().optional(),

  // Identifiants administratifs
  siren: sirenSchema,
  siret_siege: siretSchema,
  tva_intracommunautaire: tvaIntraSchema,
  numero_accise: numeroAcciseSchema,
  code_naf: codeNafSchema,

  // Adresse siège
  adresse_ligne_1: optionalTrim(255),
  adresse_ligne_2: optionalTrim(255),
  code_postal: codePostalSchema,
  commune: optionalTrim(120),
  commune_insee: communeInseeSchema,
});
export type UpdateMyDomaineInput = z.infer<typeof updateMyDomaineInput>;
